import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Button,
  Tab,
  Tabs,
  Card,
  CardContent,
  Alert,
  Avatar,
  Breadcrumbs,
  Link as MuiLink,
  Tooltip,
  Snackbar,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Apps as AppsIcon,
  Code as CodeIcon,
  NetworkCheck as NetworkCheckIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as NeverRunIcon,
  Error as ErrorIcon,
  Login as LoginIcon,
  Sync as SyncIcon,
  TrendingUp as ProgressIcon,
  PersonRemove as DeRosterIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import SyncBadge from "../components/SyncBadge";
import appRegistryService from "../services/api/appRegistryService";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  fetchClients,
  fetchClientApps,
  fetchClientScripts,
  testClientConnection,
} from "../redux/thunks/clientsThunks";
import { setSelectedClient } from "../redux/slices/clientsSlice";

const SCRIPT_TYPE_ICONS = {
  login: <LoginIcon fontSize="small" />,
  roster: <SyncIcon fontSize="small" />,
  progress: <ProgressIcon fontSize="small" />,
  "de-roster": <DeRosterIcon fontSize="small" />,
  de_roster: <DeRosterIcon fontSize="small" />,
};

const STATUS_COLORS = {
  success: "success",
  failed: "error",
  error: "error",
  running: "warning",
  never_run: "default",
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

/**
 * [stat-cards-fix] Human-readable "as of N ago" renderer for stat-card subtitles.
 * Returns null when no timestamp — caller shows no subtitle.
 */
function formatRelative(timestamp) {
  if (!timestamp) return null;
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `as of ${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `as of ${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `as of ${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `as of ${diffDay}d ago`;
}

/**
 * [stat-cards-fix] Stat card with four render states:
 *   - loading  → MUI Skeleton (not bare "--")
 *   - value    → big number + label + optional "as of Nm ago"
 *   - unavailable → "N/A" in muted color + tooltip explaining why
 *
 * Props:
 *   label          — "Users" / "Apps" / "Facilities"
 *   value          — number | null | undefined
 *   loading        — boolean
 *   unavailable    — boolean (e.g. no Metabase DB mapped)
 *   unavailableReason — tooltip text shown when unavailable
 *   asOf           — ISO timestamp for "as of N ago" subtitle
 */
function StatCard({ label, value, loading, unavailable, unavailableReason, asOf }) {
  let content;
  if (loading) {
    content = (
      <>
        <Skeleton variant="text" width={60} height={48} />
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </>
    );
  } else if (unavailable || value === null || value === undefined) {
    const body = (
      <>
        <Typography variant="h4" color="text.disabled">N/A</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </>
    );
    content = unavailableReason ? (
      <Tooltip title={unavailableReason} arrow>
        <Box>{body}</Box>
      </Tooltip>
    ) : body;
  } else {
    const relative = formatRelative(asOf);
    content = (
      <>
        <Typography variant="h4" color="primary">{value}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        {relative && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            {relative}
          </Typography>
        )}
      </>
    );
  }
  return (
    <Card sx={{ minWidth: 140 }}>
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        {content}
      </CardContent>
    </Card>
  );
}

// Per-client click debounce (ms) — complements the server-side per-client lock.
const PER_CLIENT_DEBOUNCE_MS = 15_000;
// Sync-job poll interval (ms).
const POLL_INTERVAL_MS = 1500;
// Max polls before giving up UI progress.
const MAX_POLLS = 40;

const ClientDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [tab, setTab] = useState(0);

  // Per-client refresh state
  const [syncing, setSyncing] = useState(false);
  const [syncCooldownUntil, setSyncCooldownUntil] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [snackbar, setSnackbar] = useState(null);

  const {
    clients,
    selectedClient,
    clientApps,
    clientScripts,
    connectionStatus,
    loading,
    appsLoading,
    appsError, // [9a-endpoint-fix]
    scriptsLoading,
    connectionLoading,
  } = useSelector((state) => state.clients);

  // Load client list if not yet loaded, then select client
  useEffect(() => {
    if (clients.length === 0) {
      dispatch(fetchClients());
    }
  }, [dispatch, clients.length]);

  useEffect(() => {
    if (clients.length > 0 && id) {
      const client = clients.find(
        (c) => String(c.id) === String(id) || c.tag === id
      );
      if (client) {
        dispatch(setSelectedClient(client));
      }
    }
  }, [dispatch, clients, id]);

  // Load detail data when client is selected.
  // [stat-cards-fix] Dropped fetchClientStats — that endpoint hits the broken
  // direct-Postgres path. Users + Facilities now ride the AppCatalogSync
  // pipeline and are served as columns on the Client record itself.
  useEffect(() => {
    if (selectedClient) {
      const clientId = selectedClient.id || selectedClient.tag;
      dispatch(fetchClientApps(clientId));
      dispatch(fetchClientScripts(clientId));
    }
  }, [dispatch, selectedClient]);

  const handleTestConnection = () => {
    if (selectedClient) {
      dispatch(testClientConnection(selectedClient.id || selectedClient.tag));
    }
  };

  // Tick cooldown display every second
  useEffect(() => {
    if (!syncCooldownUntil) {
      setCooldownRemaining(0);
      return undefined;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((syncCooldownUntil - Date.now()) / 1000));
      setCooldownRemaining(remaining);
      if (remaining === 0) setSyncCooldownUntil(0);
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [syncCooldownUntil]);

  const pollJob = async (jobId) => {
    for (let i = 0; i < MAX_POLLS; i++) {
      try {
        const res = await appRegistryService.getSyncJob(jobId);
        const state = res?.data?.state;
        if (state === "completed") return { ok: true, result: res.data };
        if (state === "failed")
          return { ok: false, error: res.data.failedReason || "Job failed" };
      } catch (e) {
        // keep trying
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    return { ok: false, error: "Timed out waiting for sync job" };
  };

  const handleRefreshClient = async () => {
    if (!selectedClient || !selectedClient.id) return;
    if (Date.now() < syncCooldownUntil) return;
    setSyncing(true);
    try {
      const res = await appRegistryService.triggerSyncForClient(selectedClient.id);
      if (res.status === 429) {
        const secs = Math.ceil((res.data.retryAfterMs || PER_CLIENT_DEBOUNCE_MS) / 1000);
        setSnackbar({ severity: "warning", msg: `Refresh debounced — try again in ${secs}s` });
        setSyncCooldownUntil(Date.now() + secs * 1000);
        return;
      }
      const jobId = res.data?.jobId;
      setSyncCooldownUntil(Date.now() + PER_CLIENT_DEBOUNCE_MS);
      setSnackbar({
        severity: "info",
        msg: `Syncing ${selectedClient.displayName || selectedClient.name}… (job ${jobId})`,
      });
      if (jobId) {
        const outcome = await pollJob(String(jobId));
        if (outcome.ok) {
          const summary = outcome.result?.returnvalue || {};
          const appsSeen = summary.appsSeen ?? "?";
          setSnackbar({
            severity: "success",
            msg: `Sync complete — ${appsSeen} apps (source: ${summary.source || "unknown"})`,
          });
          // Refresh page data. fetchClients() refreshes the client record,
          // which is where usersCount / facilitiesCount / lastStatsSyncAt
          // now live (populated by AppCatalogSyncService during this sync).
          dispatch(fetchClients());
          dispatch(fetchClientApps(selectedClient.id || selectedClient.tag));
        } else {
          setSnackbar({ severity: "error", msg: `Sync failed: ${outcome.error}` });
        }
      }
    } catch (e) {
      setSnackbar({
        severity: "error",
        msg: `Refresh failed: ${e.response?.data?.message || e.message}`,
      });
    } finally {
      setSyncing(false);
    }
  };

  // Look up app display name + logo by configKey (from the apps tab data)
  const appsByConfigKey = useMemo(() => {
    const map = {};
    (clientApps || []).forEach((a) => {
      if (a.configKey) map[a.configKey] = a;
    });
    return map;
  }, [clientApps]);

  // Group scripts by app. Prefer the enriched app name from clientApps when available.
  const scriptsByApp = useMemo(() => {
    const groups = {};
    (clientScripts || []).forEach((script) => {
      const configKey = script.configKey || script.appKey || script.app;
      const appMeta = configKey ? appsByConfigKey[configKey] : null;
      const appName =
        appMeta?.displayName || appMeta?.name || script.appName || configKey || "Unknown App";
      const key = `${configKey || "__unknown"}::${appName}`;
      if (!groups[key]) groups[key] = { appName, configKey, logo: appMeta?.logo || null, scripts: [] };
      groups[key].scripts.push(script);
    });
    return groups;
  }, [clientScripts, appsByConfigKey]);

  if (loading && !selectedClient) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedClient && !loading) {
    return (
      <Container maxWidth="xl">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/clients")} sx={{ mb: 2 }}>
          Back to Clients
        </Button>
        <Alert severity="warning">Client not found.</Alert>
      </Container>
    );
  }

  if (!selectedClient) return null;

  const env = (selectedClient.environment || "unknown").toLowerCase();
  const isActive = selectedClient.isActive !== false && selectedClient.status !== "inactive";

  return (
    <Container maxWidth="xl">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/clients" underline="hover" color="inherit">
          Clients
        </MuiLink>
        <Typography color="text.primary">
          {selectedClient.displayName || selectedClient.name}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <BusinessIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">
            {selectedClient.displayName || selectedClient.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Chip label={selectedClient.tag} size="small" variant="outlined" />
            <Chip
              label={env}
              size="small"
              color={
                env === "prod" || env === "production" ? "error" :
                env === "staging" ? "warning" : "info"
              }
              variant="outlined"
            />
            <Chip
              icon={isActive ? <CheckCircleIcon /> : <CancelIcon />}
              label={isActive ? "Active" : "Inactive"}
              size="small"
              color={isActive ? "success" : "error"}
              variant="outlined"
            />
            <SyncBadge
              lastSyncAt={selectedClient.lastSyncAt}
              metabaseDatabaseId={selectedClient.metabaseDatabaseId}
            />
          </Stack>
        </Box>
        <Tooltip
          title={
            cooldownRemaining > 0
              ? `Wait ${cooldownRemaining}s before re-syncing this client`
              : "Trigger an App Catalog sync for just this client (15s debounce)"
          }
        >
          <span>
            <Button
              data-testid="refresh-this-client-btn"
              variant="outlined"
              color="primary"
              startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefreshClient}
              disabled={syncing || cooldownRemaining > 0}
            >
              {syncing
                ? "Syncing…"
                : cooldownRemaining > 0
                ? `Wait ${cooldownRemaining}s`
                : "Refresh this client"}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Unmapped-client banner: no Metabase DB → falls to mock, very few apps */}
      {(selectedClient.metabaseDatabaseId == null ||
        selectedClient.metabaseDatabaseId === undefined) && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="unmapped-client-banner">
          <strong>No Metabase database assigned.</strong> App catalog sync for this
          client falls through to the local mock path (≈5 apps only). To enable real
          sync, assign a <code>metabaseDatabaseId</code> via admin, or call{" "}
          <code>PUT /api/clients/{selectedClient.id}</code> with{" "}
          <code>{`{ "metabaseDatabaseId": <N> }`}</code>.
        </Alert>
      )}

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.msg}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Stats Row */}
      {/* [stat-cards-fix] Users + Facilities read from selectedClient columns
          populated by AppCatalogSyncService via Metabase (replaces the broken
          /clients/:id/stats Azure-privatelink path). Apps count comes from
          the already-working app-registry matrix via clientApps.length —
          same source as the Apps tab, so the numbers always match. */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <StatCard
          label="Users"
          value={selectedClient.usersCount}
          loading={loading}
          unavailable={selectedClient.metabaseDatabaseId == null}
          unavailableReason="No Metabase database mapped for this client — assign metabaseDatabaseId to enable user counts."
          asOf={selectedClient.lastStatsSyncAt}
        />
        <StatCard
          label="Apps"
          value={appsLoading ? null : clientApps.length}
          loading={appsLoading}
          unavailable={Boolean(appsError)}
          unavailableReason={appsError ? `Apps unavailable: ${appsError}` : null}
          asOf={selectedClient.lastSyncAt}
        />
        <StatCard
          label="Facilities"
          value={selectedClient.facilitiesCount}
          loading={loading}
          unavailable={selectedClient.metabaseDatabaseId == null}
          unavailableReason="No Metabase database mapped for this client — assign metabaseDatabaseId to enable facility counts."
          asOf={selectedClient.lastStatsSyncAt}
        />
        <Card sx={{ minWidth: 200 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={connectionLoading ? <CircularProgress size={16} /> : <NetworkCheckIcon />}
              onClick={handleTestConnection}
              disabled={connectionLoading}
              sx={{ mb: 0.5 }}
            >
              Test Connection
            </Button>
            {connectionStatus && (
              <Typography
                variant="caption"
                display="block"
                color={connectionStatus.reachable ? "success.main" : "error.main"}
              >
                {connectionStatus.reachable ? "Reachable" : connectionStatus.error || "Connection unavailable"}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Recent sync info (first-pass; BullMQ history wiring deferred) */}
      {selectedClient.lastSyncAt && (
        <Card sx={{ mb: 2 }} data-testid="recent-sync-card">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="subtitle2" gutterBottom>
              Recent sync
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last sync: {new Date(selectedClient.lastSyncAt).toLocaleString()}
              {selectedClient.updatedAt && (
                <> &nbsp;·&nbsp; Client record updated: {new Date(selectedClient.updatedAt).toLocaleString()}</>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Full run history (duration, apps-synced count) available via{" "}
              <code>GET /api/app-catalog/sync/status</code>.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper elevation={1}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab icon={<AppsIcon />} label={`Apps (${clientApps.length})`} iconPosition="start" />
          <Tab icon={<CodeIcon />} label={`Scripts (${(clientScripts || []).length})`} iconPosition="start" />
        </Tabs>

        {/* Apps Tab */}
        <TabPanel value={tab} index={0}>
          {appsLoading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : appsError ? (
            /* [9a-endpoint-fix] Surface load failures instead of masking as 0 */
            <Box sx={{ p: 3 }}>
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() =>
                      dispatch(
                        fetchClientApps(selectedClient.id || selectedClient.tag)
                      )
                    }
                  >
                    Retry
                  </Button>
                }
              >
                Failed to load apps: {appsError}
              </Alert>
            </Box>
          ) : clientApps.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell><strong>Application</strong></TableCell>
                    <TableCell><strong>Config Key</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>SSO Type</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...clientApps]
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((app, idx) => {
                      const appActive = app.isActive !== false && app.status !== "inactive";
                      const canNavigate = Boolean(app.configKey);
                      return (
                        <TableRow
                          key={app.id || app.configKey || idx}
                          hover
                          sx={{ cursor: canNavigate ? "pointer" : "default" }}
                          onClick={
                            canNavigate
                              ? () => navigate(`/apps/${app.configKey}`)
                              : undefined
                          }
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              {app.logo ? (
                                <Avatar
                                  src={app.logo}
                                  alt={app.name || app.configKey}
                                  variant="rounded"
                                  sx={{ width: 32, height: 32, bgcolor: "grey.100" }}
                                  imgProps={{
                                    onError: (e) => {
                                      // Hide the broken image; MUI Avatar then renders the AppsIcon fallback.
                                      e.target.style.display = "none";
                                    },
                                  }}
                                >
                                  <AppsIcon fontSize="small" />
                                </Avatar>
                              ) : (
                                <Avatar
                                  variant="rounded"
                                  sx={{ width: 32, height: 32, bgcolor: "primary.light" }}
                                >
                                  <AppsIcon fontSize="small" />
                                </Avatar>
                              )}
                              <Typography variant="body2" fontWeight="bold">
                                {app.displayName || app.name}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                              {app.configKey}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={appActive ? <CheckCircleIcon /> : <CancelIcon />}
                              label={appActive ? "Active" : "Inactive"}
                              size="small"
                              color={appActive ? "success" : "error"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={app.ssoType || "N/A"} size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* [9a-endpoint-fix] Empty state — real zero apps synced */
            <Box sx={{ p: 3 }}>
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleRefreshClient}
                    disabled={syncing || cooldownRemaining > 0}
                  >
                    {cooldownRemaining > 0 ? `Refresh (${cooldownRemaining}s)` : "Refresh this client"}
                  </Button>
                }
              >
                No apps synced yet for this client. Try the refresh button, or
                check that the client has a <code>metabaseDatabaseId</code> assigned.
              </Alert>
            </Box>
          )}
        </TabPanel>

        {/* Scripts Tab */}
        <TabPanel value={tab} index={1}>
          {scriptsLoading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : Object.keys(scriptsByApp).length > 0 ? (
            <Box sx={{ p: 2 }}>
              {Object.entries(scriptsByApp).map(([groupKey, group]) => (
                <Box key={groupKey} sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    {group.logo ? (
                      <Avatar
                        src={group.logo}
                        alt={group.appName}
                        variant="rounded"
                        sx={{ width: 28, height: 28, bgcolor: "grey.100" }}
                        imgProps={{
                          onError: (e) => {
                            e.target.style.display = "none";
                          },
                        }}
                      >
                        <AppsIcon fontSize="small" />
                      </Avatar>
                    ) : (
                      <Avatar
                        variant="rounded"
                        sx={{ width: 28, height: 28, bgcolor: "primary.light" }}
                      >
                        <AppsIcon fontSize="small" />
                      </Avatar>
                    )}
                    {group.configKey ? (
                      <MuiLink
                        component={Link}
                        to={`/apps/${group.configKey}`}
                        underline="hover"
                        variant="subtitle1"
                        fontWeight="bold"
                        color="inherit"
                      >
                        {group.appName}
                      </MuiLink>
                    ) : (
                      <Typography variant="subtitle1" fontWeight="bold">
                        {group.appName}
                      </Typography>
                    )}
                    <Chip
                      label={`${group.scripts.length} script${group.scripts.length === 1 ? "" : "s"}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.50" }}>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell><strong>Script Type</strong></TableCell>
                          <TableCell><strong>Last Run</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Version</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.scripts.map((script, idx) => {
                          const scriptType = (script.type || script.scriptType || "unknown")
                            .replace(/_/g, "-");
                          const lastStatus = script.lastRunStatus || script.status || "never_run";
                          const lastRun = script.lastRunAt || script.lastRunTime;
                          return (
                            <TableRow
                              key={script.id || idx}
                              hover
                              sx={{ cursor: script.id ? "pointer" : "default" }}
                              onClick={
                                script.id
                                  ? () => navigate(`/scripts/${script.id}/edit`)
                                  : undefined
                              }
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {script.name || "(unnamed)"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {SCRIPT_TYPE_ICONS[scriptType] || <CodeIcon fontSize="small" />}
                                  <Chip
                                    label={scriptType}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontSize="0.8rem">
                                  {lastRun
                                    ? new Date(lastRun).toLocaleString()
                                    : "Never"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <StatusChip status={lastStatus} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontSize="0.8rem">
                                  {script.version ? `v${script.version}` : "--"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                No scripts found for this client. Scripts are derived from the apps
                enabled for this client — add app scripts under Extension Scripts,
                or ensure the relevant apps are enabled on this client.
              </Alert>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

const StatusChip = ({ status }) => {
  const normalized = (status || "never_run").toLowerCase().replace(/\s+/g, "_");
  const color = STATUS_COLORS[normalized] || "default";
  const icon =
    normalized === "success" ? <CheckCircleIcon /> :
    normalized === "failed" || normalized === "error" ? <ErrorIcon /> :
    normalized === "running" ? <CircularProgress size={14} /> :
    <NeverRunIcon />;

  const label =
    normalized === "never_run" ? "Never Run" :
    normalized.charAt(0).toUpperCase() + normalized.slice(1);

  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      color={color}
      variant="outlined"
    />
  );
};

export default ClientDetail;
